import { CardSection } from "~/components/atoms/CardSection";
import { CardSectionRow } from "~/components/atoms/CardSectionRow";
import { CollegeData } from "~/components/pages/CollegePage";
import {
    Modal,
    ModalContent,
    ModalDescription,
    ModalHeader,
    ModalTitle,
    ModalTrigger,
} from "~/components/molecules/Modal";
import magnifyingGlass from "~/assets/icon-magnifying-glass.svg";
import Image from "~/components/atoms/Image";
import { SetStateAction, useState } from "react";
import { FaX } from "react-icons/fa6";
import { cn } from "~/lib/utils";
import { Testable } from "~/lib/testing";

function List({ majors }: Pick<CollegeData, "majors">) {
    return majors.length > 0 ? (
        <>
            {majors.map(m => (
                <CardSectionRow key={m.id} left={m.attrs.name} />
            ))}
        </>
    ) : (
        <p className="font-metadata-medium p-3 text-gray-80">No majors found</p>
    );
}

function Typeahead({
    value,
    setValue,
    "data-testid": testId,
}: {
    value: string;
    setValue: React.Dispatch<SetStateAction<string>>;
} & Testable) {
    const [isFocused, setFocused] = useState(false);
    return (
        <div
            className={cn(
                "flex h-10 w-full items-center gap-2 rounded-md border px-3",
                isFocused ? "border-2 border-blue-80" : "border-gray-40",
            )}
        >
            <Image src={magnifyingGlass} alt="magnifying glass icon" className="size-4" />
            <input
                data-testid={testId}
                className="font-body w-full focus:outline-none"
                onBlur={() => setFocused(false)}
                onChange={e => setValue(e.target.value)}
                onFocus={() => setFocused(true)}
                placeholder="Search for a major"
                value={value}
            />
            {value.length > 0 && (
                <button onClick={() => setValue("")}>
                    <FaX className="size-2.5 text-blue-80" />
                </button>
            )}
        </div>
    );
}

export function CollegeMajors(props: Pick<CollegeData, "majors">) {
    const [typeahead, setTypeahead] = useState("");
    if (props.majors.length <= 0) return null;
    const majors = props.majors
        .filter(m => new RegExp(typeahead, "i").test(m.attrs.name))
        .sort((a, b) => a.attrs.name.localeCompare(b.attrs.name));
    const title = "Majors";
    const testId = "college-majors-modal-content";
    const limit = 5;
    return (
        <Modal>
            <CardSection title={title}>
                <div className="h-[328px] px-6" data-testid="majors-section">
                    <Typeahead
                        value={typeahead}
                        setValue={setTypeahead}
                        data-testid="college-majors-typeahead"
                    />
                    <List majors={majors.slice(0, limit)} />
                    {majors.length > limit && (
                        <ModalTrigger
                            className="font-metadata-medium p-3 text-blue-80"
                            data-testid="show-more-button-majors-section"
                            aria-controls={testId}
                        >
                            Show More
                        </ModalTrigger>
                    )}
                </div>
            </CardSection>
            <ModalContent className="size-full" data-testid={testId}>
                <ModalDescription>View College Majors</ModalDescription>
                <ModalHeader>
                    <ModalTitle>{title}</ModalTitle>
                    <Typeahead
                        value={typeahead}
                        setValue={setTypeahead}
                        data-testid="college-majors-modal-typeahead"
                    />
                </ModalHeader>
                <List majors={majors} />
            </ModalContent>
        </Modal>
    );
}
